import * as React from 'react'
import { cva, cx, type VariantProps } from 'class-variance-authority'
import { DynamicIcon } from '@/components/atoms/DynamicIcon/dynamicIcon.atom'

const alertRootVariants = cva(
  'relative w-full rounded-md border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>a]:pt-2 [&>a]:text-sm [&>a]:block [&>a]:underline [&>a]:underline-offset-2 hover:[&>a]:text-slate-300',
  {
    variants: {
      variant: {
        default: 'bg-primary-500/25 text-slate-700 border-primary-500/50',
        info: 'bg-cyan-200/25 text-slate-700 border-cyan-200/50',
        success: 'bg-green-200/25 text-slate-700 border-green-200/75',
        destructive:
          'border-destructive/50 bg-destructive text-neutral-50 dark:border-destructive [&>svg]:text-destructive hover:[&>a]:text-neutral-300',
        warning: 'bg-yellow-200/25 text-slate-700 border-yellow-200/50',
        neutral: 'bg-neutral-200/25 text-slate-700 border-neutral-200/50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
)

type DismissVisibility = 'unknown' | 'visible' | 'hidden'

const DEFAULT_DISMISS_STORAGE_KEY = 'onboarding-alerts'

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x): x is string => typeof x === 'string')

const readDismissedKeys = (storageKey: string): Set<string> => {
  if (typeof window === 'undefined') return new Set()

  try {
    const raw = window.localStorage.getItem(storageKey)
    if (!raw) return new Set()

    const parsed: unknown = JSON.parse(raw)

    if (isStringArray(parsed)) return new Set(parsed)

    return new Set()
  } catch {
    return new Set()
  }
}

const writeDismissedKeys = (storageKey: string, keys: Set<string>) => {
  if (typeof window === 'undefined') return

  try {
    // Guard against unbounded growth
    const capped = Array.from(keys).slice(-200)
    window.localStorage.setItem(storageKey, JSON.stringify(capped))
  } catch {
    // Ignore quota / blocked storage
  }
}

export type AlertRootProps = React.HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertRootVariants> & {
    /**
     * When provided, shows a top-right dismiss (X) button and persists the dismissal.
     * Dismissed alerts do not render after mount.
     */
    dismissKey?: string
    /**
     * Storage key used to persist dismissed alerts as a JSON array of strings.
     * Defaults to "onboarding-alerts".
     */
    dismissStorageKey?: string
    /** Accessibility label for the dismiss button. */
    dismissAriaLabel?: string
    /** Optional callback fired after persisting the dismissal. */
    onDismiss?: (dismissKey: string) => void
  }

const AlertRoot = React.forwardRef<HTMLDivElement, AlertRootProps>(
  (props, ref) => {
    const {
      className,
      variant,
      dismissKey,
      dismissStorageKey = DEFAULT_DISMISS_STORAGE_KEY,
      dismissAriaLabel = 'Dismiss alert',
      onDismiss,
      children,
      ...rest
    } = props

    // Important: start as "unknown" when dismissKey is provided to avoid SSR mismatch.
    const [visibility, setVisibility] = React.useState<DismissVisibility>(() =>
      dismissKey ? 'unknown' : 'visible',
    )

    React.useEffect(() => {
      if (!dismissKey) return

      const dismissed = readDismissedKeys(dismissStorageKey).has(dismissKey)
      setVisibility(dismissed ? 'hidden' : 'visible')
    }, [dismissKey, dismissStorageKey])

    const handleDismiss = React.useCallback(() => {
      if (!dismissKey) return

      const keys = readDismissedKeys(dismissStorageKey)
      keys.add(dismissKey)
      writeDismissedKeys(dismissStorageKey, keys)
      setVisibility('hidden')
      onDismiss?.(dismissKey)
    }, [dismissKey, dismissStorageKey, onDismiss])

    if (dismissKey && visibility === 'hidden') return null

    return (
      <div
        ref={ref}
        role='alert'
        className={cx(
          alertRootVariants({ variant }),
          dismissKey ? 'pr-10' : undefined,
          className,
        )}
        {...rest}
      >
        {dismissKey ? (
          <button
            type='button'
            aria-label={dismissAriaLabel}
            onClick={handleDismiss}
            className='absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-sm text-slate-700/80 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2'
          >
            <DynamicIcon name='X' className='h-4 w-4' />
          </button>
        ) : null}

        {children}
      </div>
    )
  },
)
AlertRoot.displayName = 'Alert'

type AlertTitleProps = React.HTMLAttributes<HTMLHeadingElement> & {
  className?: string
}

const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(
  (props, ref) => {
    const { className, ...rest } = props
    return (
      <h5
        ref={ref}
        className={cx(
          'mb-2 font-medium leading-none tracking-tight',
          className,
        )}
        {...rest}
      />
    )
  },
)
AlertTitle.displayName = 'AlertTitle'

type AlertDescriptionProps = React.HTMLAttributes<HTMLParagraphElement> & {
  className?: string
}

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  AlertDescriptionProps
>((props, ref) => {
  const { className, ...rest } = props
  return (
    <div
      ref={ref}
      className={cx('text-sm [&_p]:leading-relaxed', className)}
      {...rest}
    />
  )
})
AlertDescription.displayName = 'AlertDescription'

export const Alert: {
  Root: typeof AlertRoot
  Title: typeof AlertTitle
  Description: typeof AlertDescription
} = {
  Root: AlertRoot,
  Title: AlertTitle,
  Description: AlertDescription,
}
